<template>
  <div class="card card-chat-booking float-left ml-3">
    <div class="card-body">
      <!-- Booking Plan -->
      <div class="text-remark">
        <booking-plan :packageType="packageType"
                      @selectPackageType="selectPackageType"
                      v-if="parentService && parentService.can_book_package === 1"/>
      </div>
      <!-- Booking with no date-time selection -->
      <section v-if="Number(bookingData.professional_id) === 0 && (parentService && !parentService.is_experience_service) && (user && user.flagBlack === 0)">
        <!-- Single booking type -->
        <div v-if="!packageType">
          <b>{{ $t('message.booking.new_booking.select_booking_type') }}</b>
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="exp">
                <div class="checkbox">
                  <input type="radio"
                         name="bookingType"
                         id="normal_booking"
                         value="normal"
                         v-model="bookingType"/>
                  <label class="mb-0" for="normal_booking">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.booking.new_booking.booking_type_normal') }}
                  </label>
                </div>
              </div>
              <div class="exp pt-4">
                <div class="checkbox">
                  <input type="radio"
                         name="bookingType"
                         id="flexible_booking"
                         value="flexible"
                         v-model="bookingType"/>
                  <label class="mb-0" for="flexible_booking">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.booking.new_booking.booking_type_flexible') }}
                    <div class="d-inline-block text-comment text-secondary pt-1" style="padding-left: 2.2rem">
                      {{ $t('message.booking.new_booking.booking_type_flexible_remark') }}
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Package booking type -->
        <div v-else>
          <b>{{ $t('message.booking.new_booking.select_package_type') }}</b>
          <div class="card shadow-sm">
            <div class="card-body">
              <div class="exp">
                <div class="checkbox">
                  <input type="checkbox"
                         id="package_unlimited"
                         @change="selectPackageType('unlimited')"
                         :checked="packageType && packageType.category === 'unlimited'"/>
                  <label class="mb-0" for="package_unlimited">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.booking.new_booking.package_type_unlimited') }}
                  </label>
                </div>
              </div>
              <div class="exp pt-4">
                <div class="checkbox">
                  <input type="checkbox"
                         id="package_flexible"
                         @change="selectPackageType('flexible')"
                         :checked="packageType && packageType.category === 'flexible'"/>
                  <label class="mb-0" for="package_flexible">
                    <span><!-- This span is needed to create the "checkbox" element --></span>
                    {{ $t('message.booking.new_booking.package_type_flexible') }}
                    <div class="d-inline-block text-comment text-secondary pt-1" style="padding-left: 2.2rem">
                      {{ $t('message.booking.new_booking.booking_type_flexible_remark') }}
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-show="(bookingType === 'normal' && !packageType) || (packageType && packageType.category === 'unlimited')">
        <div class="my-3">
          <b>{{ message.text }}</b>
        </div>
        <!-- Date picker section -->
        <div v-if="packageType && packageType.category === 'unlimited'">
          <!-- Package with selected professional -->
          <section class="text-remark" v-if="professionalId">
            <professional-multi-dates-calendar
              @updateData="updateProfessionalMultiDates"
              @updateNumPackageSelectedDates="updateNumPackageSelectedDates"
              :professionalId="bookingData.professional_id"
              :maxNum="packageType.num_per_package"
              :workHours="bookingData.work_hour"
              :packageType="packageType"/>
          </section>
          <!-- Package with no professional -->
          <section v-else>
            <multi-dates-calendar
              :maxNum="packageType.num_per_package"
              :serviceId="(parentService) ? parentService.id : 1"
              :sameDayBookingConfig="sameDayBookingConfig"
              @selectDate="onDateChange"/>
            <section class="mt-4 text-remark" v-if="multiDates && multiDates.length > 0">
              <package-discount :numDates="multiDates.length"
                                :packageType="packageType"
                                @selectPackageTypeDiscount="selectPackageTypeDiscount"/>
              <span class="badge badge-pill py-2 px-3 mr-2 mb-2 badge-light" v-for="date in multiDates">
            {{ date | shortDate }}
          </span>
            </section>
            <div v-if="errorBookingDate" class="form-alert-text -status-alert">{{ errorBookingDateText }}</div>
          </section>
        </div>
        <div v-else>
          <div class="multiDatesCalendar datepicker-here"></div>
          <div class="help text-danger mt-2" v-if="hasErrors.indexOf('date') > -1">กรุณาเลือกวัน</div>
        </div>

        <!-- Time selection -->
        <section v-if="(packageType && professionalId === 0) || !packageType">
          <div v-if="availableTimes.length > 0">
            <div class="mt-3 text-center">
              <b>เวลา</b>
            </div>

            <div class="row no-gutters mt-2">
              <div class="col-md-3 col-4 p-1" v-for="(time, index) in availableTimes">
                <button class="btn btn-outline-info btn-block btn-small-group text-center"
                        :class="{'active': selectedTime === time}"
                        @click="setSelectedTime(time)">{{ time }}
                </button>
              </div>
            </div>
            <div class="help text-danger mt-2" v-if="hasErrors.indexOf('time') > -1">กรุณาเลือกเวลา</div>
          </div>
        </section>
      </section>

      <!-- Package type discounts -->
      <div class="my-3" v-if="packageType && packageType.category === 'flexible'">
        <b class="d-inline-block pb-2">{{ $t('message.booking.new_booking.how_many_package') }}</b>
        <div class="select-jumbo">
          <select v-model="packageTypeDiscount">
            <option :value="packageTypeDiscount"
                    v-for="packageTypeDiscount in packageType.package_type_discounts">
              {{ packageTypeDiscount | multiLangName }}
            </option>
          </select>
        </div>
      </div>

      <div class="form-group mt-3">
        <button class="btn btn-block btn-info" :disabled="validateForm()"
                @click="submit">ยืนยัน
        </button>
        <div class="mt-3"
             v-if="route === 'select-professional-first' && bookingData.route === 'select-professional-first'">
          <hr>
          <div class="text-remark">
            หากต้องการจองบริการวันนี้ หรือเช็คแล้วตารางผู้ให้บริการไม่ตรงกับวันเวลาที่คุณต้องการ
            กรุณาติดต่อแอดมินเพื่อช่วยประสานงานให้ค่ะ
          </div>
          <div class="mt-3 text-center">
            <a href="https://line.me/R/ti/p/%40hnk7011n"
               target="_blank"
               class="text-center text-primary-link">
              ติดต่อแอดมิน
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import 'air-datepicker'
  import 'air-datepicker/dist/js/i18n/datepicker.en.js'
  import timeHelpers from '../../../helpers/timeHelpers'
  import moment from 'moment-timezone'
  import axios from 'axios'
  import BookingPlan from '../../booking/newSteps/components/BookingPlan'
  import PackageDiscount from '../../booking/newSteps/components/PackageDiscount'
  import serviceHelpers from '../../../helpers/serviceHelpers'

  export default {
    props: {
      message: Object
    },
    components: {
      BookingPlan,
      PackageDiscount
    },
    data () {
      return {
        language: 'th',
        hasErrors: [],
        professionalId: 0,
        workHours: null,
        selectedDate: null,
        selectedTime: null,
        datePicker: null,
        professionalCalendarJobs: null,
        availableTimes: [],
        fullBlockedDates: [],
        partialBlockedDates: [],
        cleaningDates: [],
        fullyBookedDates: [],
        route: null,
        packageType: null,
        packageTypeDiscount: null,
        multiDates: null,
        errorBookingDate: false,
        errorBookingDateText: '',
        mapDatesTimes: {},
        numPackageSelectedDates: 0,
        bookingType: 'normal',
        parentService: null,
        sameDayBookingConfig: null
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        user: 'auth/getUser'
      }),
      ...mapState('chatBooking', ['bookingData'])
    },
    watch: {
      bookingType () {
        if (this.bookingType === 'normal') {
          this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {is_no_datetime_booking: 0})
        } else if (this.bookingType === 'flexible') {
          this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {cleaning_date: null})
          this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {cleaning_time: null})
          this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {professional_id: 0})
          this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {is_no_datetime_booking: 1})
        }
      },
      packageType () {
        if (this.packageType) {
          this.$store.commit('chatBooking/REMOVE_BOOKING_DATA', 'cleaning_date')
          this.$store.commit('chatBooking/REMOVE_BOOKING_DATA', 'cleaning_time')
          if (this.packageType.category === 'flexible') {
            this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {selected_dates_times: null})
            this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {professional_id: 0})
            this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {is_no_datetime_booking: 1})
          } else if (this.packageType.category === 'unlimited') {
            this.$store.commit('chatBooking/UPDATE_BOOKING_DATA', {is_no_datetime_booking: 0})
          }
        } else {
          this.$store.commit('chatBooking/REMOVE_BOOKING_DATA', 'selected_dates_times')
        }
      }
    },
    methods: {
      updateProfessionalMultiDates (mapDatesTimes) {
        this.mapDatesTimes = mapDatesTimes
      },
      updateNumPackageSelectedDates (numDates) {
        this.numPackageSelectedDates = numDates
      },
      validateForm () {
        let enable = false
        if ((this.bookingType === 'flexible' && !this.packageType) || (this.packageType && this.packageType.category === 'flexible')) {
          enable = true
        } else if ((this.bookingType === 'normal' && !this.packageType) || (this.packageType && this.packageType.category === 'unlimited')) {
          if (this.packageType) {
            if (this.professionalId > 0 && this.numPackageSelectedDates > 0) {
              enable = (this.numPackageSelectedDates >= this.packageType.min_qty)
            } else if (this.professionalId === 0 && this.multiDates) {
              const numDates = this.multiDates.length
              enable = (numDates >= this.packageType.min_qty && this.selectedTime !== null)
            }
          } else {
            enable = (this.selectedDate !== null && this.selectedTime !== null)
          }
        }
        return (!enable)
      },
      onDateChange (selectedDate, datePicker) {
        this.multiDates = datePicker
        const numDates = this.multiDates.length
        if (numDates >= this.packageType.min_qty && numDates <= this.packageType.num_per_package) {
          this.errorBookingDate = false
          const currentHour = Number(moment().format('H'))
          if (currentHour >= this.sameDayBookingConfig.can_book_to_hour) {
            const nextAvailableTime = '10:00'
            this.setAvailableTimes(nextAvailableTime)
          } else {
            this.availableTimes = this.globalState.arrFullTimes
          }
        } else {
          this.availableTimes = []
        }
        this.selectedTime = null
      },
      selectPackageType (selectedCategory) {
        this.mapDatesTimes = {}
        this.multiDates = null
        this.availableTimes = []
        this.selectedDate = null
        this.selectedTime = null
        this.packageType = null
        if (selectedCategory && this.globalState.packageTypes) {
          for (const packageType of this.globalState.packageTypes) {
            if (packageType.category === selectedCategory) {
              this.packageType = packageType
              if (selectedCategory === 'flexible') {
                this.packageTypeDiscount = packageType.package_type_discounts[0]
              }
              break
            }
          }
        } else {
          setTimeout(() => { this.initDatePicker() }, 250)
        }
      },
      selectPackageTypeDiscount (packageTypeDiscount) {
        this.packageTypeDiscount = packageTypeDiscount
      },
      setSelectedTime (time) {
        this.selectedTime = time
      },
      sendMessage (action) {
        // console.log('check action', action)
        const message = {
          sender_type: 'user',
          type: 'text',
          label: action.label,
          text: action.text,
          data: action.data
        }
        // console.log('check message', message)
        this.$store.commit('chatBooking/ADD_MESSAGE', message)
      },
      parseTime (s) {
        const c = s.split(':')
        return parseInt(c[0]) * 60 + parseInt(c[1])
      },
      isPartialBlockedDate (selectedDate) {
        const fmDate = moment(selectedDate).format('YYYY-MM-DD')
        for (let key in this.partialBlockedDates) {
          const bDate = this.partialBlockedDates[key]
          if (bDate.date === fmDate) {
            return {
              startTime: bDate.start_time,
              endTime: bDate.end_time
            }
          }
        }
        return null
      },
      getAvailableTimes (selectedDate) {
        let startTime = null
        let endTime = null
        let availableTimes = []
        const partialBlockedDate = this.isPartialBlockedDate(selectedDate)
        if (this.partialBlockedDates.length > 0 && partialBlockedDate) {
          startTime = partialBlockedDate.startTime
          endTime = partialBlockedDate.endTime
        }
        const allTimes = this.globalState.arrTimes
        if (startTime == null && endTime == null) {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        } else {
          for (let key in allTimes) {
            const time = allTimes[key]
            if (time >= startTime && time <= endTime && this.checkAvailableTime(selectedDate, time, endTime)) {
              availableTimes.push(time)
            }
          }
        }
        return availableTimes
      },
      checkAvailableTime (selectedDate, startTime, availableEndTime) {
        let result = true
        if (this.professionalCalendarJobs) {
          const hours = Number(this.workHours) + 0.5
          const endTime = moment(selectedDate + ' ' + startTime).add(hours, 'hours').format('HH:mm')
          for (let job of this.professionalCalendarJobs) {
            if (startTime >= job.start_time && startTime <= job.end_time) {
              result = false
              break
            } else {
              const minutes = this.parseTime(startTime) - this.parseTime(job.end_time)
              if (startTime < job.start_time && endTime >= job.start_time) {
                result = false
                break
              } else if (startTime > job.end_time && minutes < 60) {
                result = false
                break
              }
            }
          }
        }
        // Check available end time comparing with duration if the selected date is partial blocked date
        if (result === true && availableEndTime) {
          const minutes = this.parseTime(availableEndTime) - this.parseTime(startTime)
          const endMinute = 60 * Number(this.workHours)
          if (minutes < endMinute) {
            result = false
          }
        }
        return result
      },
      async fetchProfessionalCalendar () {
        let response = await axios.get('professional/calendars', {
          params: {
            professional_id: this.professionalId,
            start_date: moment().format('YYYY-MM-DD'),
            end_date: null
          }
        })
        if (!response.data.error) {
          await this.fetchSoldOutDates()
          // Blocked dates
          const blockedDates = response.data.blocked_dates
          for (let key in blockedDates) {
            const bDate = blockedDates[key]
            if (bDate.blocked_type === 'full') {
              this.fullBlockedDates.push(bDate.date)
            } else if (bDate.blocked_type === 'partial') {
              this.partialBlockedDates.push(bDate)
            }
          }
          // Cleaning dates
          const cleaningDates = response.data.cleaning_dates
          for (let key in cleaningDates) {
            const cDate = cleaningDates[key].date
            this.cleaningDates.push(cDate)
          }
        }
        this.initDatePicker()
      },
      async fetchProfessionalCalendarJobs () {
        let response = await axios.get('professional/calendars/jobs', {
          params: {
            professional_id: this.professionalId,
            cleaning_date: this.selectedDate
          }
        })
        this.professionalCalendarJobs = null
        if (!response.data.error) {
          this.professionalCalendarJobs = response.data.calendar_jobs
        }
        this.availableTimes = this.getAvailableTimes(this.selectedDate)
      },
      async fetchSoldOutDates () {
        let response = await axios.get('professional/calendars/sold-out', {
          params: {
            professional_id: this.professionalId,
            start_date: moment().format('YYYY-MM-DD'),
            work_hour: this.workHours
          }
        })
        this.fullyBookedDates = []
        if (!response.data.error) {
          this.fullyBookedDates = response.data.sold_out_dates
          this.initDatePicker()
        }
      },
      initDatePicker () {
        if (this.sameDayBookingConfig) {
          this.setDatePickerLanguage()
          const currentHour = Number(moment().format('H'))
          let minDate = moment()
          // console.log('currentHour', currentHour)
          const canBookToHour = this.sameDayBookingConfig.can_book_to_hour
          let numDay = 0
          if (this.professionalId > 0) {
            numDay = (currentHour >= canBookToHour) ? 2 : 1
          } else {
            const canBookToHour = this.sameDayBookingConfig.can_book_to_hour
            numDay = 1
            if (this.sameDayBookingConfig.can_book_same_day) {
              numDay = 0
            } else {
              numDay = (this.sameDayBookingConfig.num_next_day) ? this.sameDayBookingConfig.num_next_day : 1
            }
            if (currentHour >= canBookToHour) {
              numDay += 1
            }
          }
          if (numDay > 0) {
            minDate = moment().add(numDay, 'days')
          }
          this.datePicker = window.$('.multiDatesCalendar').datepicker({
            language: this.language,
            minDate: minDate.toDate(),
            onSelect: (fmDate, datePicker) => {
              // Don't change condition below
              if (fmDate !== null && fmDate !== '') {
                this.selectedDate = fmDate
                this.selectedTime = null
                if (this.professionalId > 0) {
                  this.fetchProfessionalCalendarJobs(this.selectedDate, datePicker)
                } else {
                  const today = moment().format('YYYY-MM-DD')
                  if (this.selectedDate === today) {
                    let nextAvailableTime = timeHelpers.getNextAvailableFullTime()
                    if (currentHour < 7) {
                      nextAvailableTime = '10:00'
                    }
                    this.setAvailableTimes(nextAvailableTime)
                  } else {
                    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD')
                    if (this.selectedDate === tomorrow && currentHour >= this.sameDayBookingConfig.can_book_to_hour) {
                      const nextAvailableTime = '10:00'
                      this.setAvailableTimes(nextAvailableTime)
                    } else {
                      this.availableTimes = this.globalState.arrFullTimes
                    }
                  }
                  this.$parent.$parent.scrollToBottom()
                }
              } else {
                this.selectedDate = null
                this.selectedTime = null
                this.availableTimes = []
              }
            },
            onRenderCell: (date, cellType) => {
              const currentDate = date.getDate()
              const fmDate = moment(date).format('YYYY-MM-DD')
              if (cellType === 'day' && (this.fullBlockedDates.indexOf(fmDate) !== -1 || this.fullyBookedDates.indexOf(fmDate) !== -1)) {
                return {
                  disabled: true
                }
              } else if (cellType === 'day' && this.cleaningDates.indexOf(fmDate) !== -1) {
                return {
                  html: currentDate + '<span class="dp-note"></span>'
                }
              }
            }
          })
        }
      },
      setDatePickerLanguage () {
        window.$.fn.datepicker.language['th'] = {
          days: [this.$i18n.t('message.calendar.week.01'), this.$i18n.t('message.calendar.week.02'), this.$i18n.t('message.calendar.week.03'), this.$i18n.t('message.calendar.week.04'), this.$i18n.t('message.calendar.week.05'), this.$i18n.t('message.calendar.week.06'), this.$i18n.t('message.calendar.week.07')],
          daysShort: [this.$i18n.t('message.calendar.week_short.01'), this.$i18n.t('message.calendar.week_short.02'), this.$i18n.t('message.calendar.week_short.03'), this.$i18n.t('message.calendar.week_short.04'), this.$i18n.t('message.calendar.week_short.05'), this.$i18n.t('message.calendar.week_short.06'), this.$i18n.t('message.calendar.week_short.07')],
          daysMin: [this.$i18n.t('message.calendar.week_short.01'), this.$i18n.t('message.calendar.week_short.02'), this.$i18n.t('message.calendar.week_short.03'), this.$i18n.t('message.calendar.week_short.04'), this.$i18n.t('message.calendar.week_short.05'), this.$i18n.t('message.calendar.week_short.06'), this.$i18n.t('message.calendar.week_short.07')],
          months: [this.$i18n.t('message.calendar.month.01'), this.$i18n.t('message.calendar.month.02'), this.$i18n.t('message.calendar.month.03'), this.$i18n.t('message.calendar.month.04'), this.$i18n.t('message.calendar.month.05'), this.$i18n.t('message.calendar.month.06'), this.$i18n.t('message.calendar.month.07'), this.$i18n.t('message.calendar.month.08'), this.$i18n.t('message.calendar.month.09'), this.$i18n.t('message.calendar.month.10'), this.$i18n.t('message.calendar.month.11'), this.$i18n.t('message.calendar.month.12')],
          monthsShort: [this.$i18n.t('message.calendar.month_short.01'), this.$i18n.t('message.calendar.month_short.02'), this.$i18n.t('message.calendar.month_short.03'), this.$i18n.t('message.calendar.month_short.04'), this.$i18n.t('message.calendar.month_short.05'), this.$i18n.t('message.calendar.month_short.06'), this.$i18n.t('message.calendar.month_short.07'), this.$i18n.t('message.calendar.month_short.08'), this.$i18n.t('message.calendar.month_short.09'), this.$i18n.t('message.calendar.month_short.10'), this.$i18n.t('message.calendar.month_short.11'), this.$i18n.t('message.calendar.month_short.12')],
          today: this.$i18n.t('message.calendar.today'),
          clear: 'Clear',
          dateFormat: 'yyyy-mm-dd',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }

        window.$.fn.datepicker.language['en'] = {
          days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
          daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
          months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
          today: 'Today',
          clear: 'Clear',
          dateFormat: 'mm/dd/yyyy',
          timeFormat: 'hh:ii aa',
          firstDay: 0
        }
      },
      setAvailableTimes (nextAvailableTime) {
        this.availableTimes = []
        if (nextAvailableTime) {
          const arrTimes = this.globalState.arrFullTimes
          let startIndex = arrTimes.indexOf(nextAvailableTime)
          if (startIndex < 3) {
            startIndex = 3
          }
          for (let i = startIndex; i < arrTimes.length; i++) {
            this.availableTimes.push(arrTimes[i])
          }
        }
      },
      validation () {
        this.hasErrors = []
        if ((this.bookingType === 'normal' && !this.packageType) || (this.packageType && this.packageType.category === 'unlimited')) {
          if (this.packageType) {
            // Remove single selected date-time
            if (this.bookingData.cleaning_date) delete this.bookingData.cleaning_date
            if (this.bookingData.cleaning_time) delete this.bookingData.cleaning_time
            // Has no professional selection
            if (this.professionalId === 0) {
              if (!this.multiDates) {
                this.hasErrors.push('multi-dates')
              }
              if (!this.selectedTime) {
                this.hasErrors.push('time')
              }
            }
            // Has professional selection
            if (this.professionalId > 0 && this.numPackageSelectedDates < this.packageType.min_qty) {
              this.hasErrors.push('multi-dates-times')
            }
          } else {
            // Remove multi selected date-times
            if (this.bookingData.selected_dates_times) delete this.bookingData.selected_dates_times
            if (this.bookingData.package_type_id) delete this.bookingData.package_type_id
            if (this.bookingData.package_type_discount_id) delete this.bookingData.package_type_discount_id

            if (!this.selectedDate) {
              this.hasErrors.push('date')
            }
            if (!this.selectedTime) {
              this.hasErrors.push('time')
            }
          }
        }
        // console.log('has errors', this.hasErrors)
        return (this.hasErrors.length === 0)
      },
      async submit () {
        if (this.validation()) {
          let action = {}
          if ((this.bookingType === 'normal' && !this.packageType) || (this.packageType && this.packageType.category === 'unlimited')) {
            // Check package booking
            if (this.packageType) {
              let multiDatesTimes = []
              let displayDatesTimes = ''
              // Has no professional selection
              if (this.professionalId === 0) {
                const sortedMultiDates = this._.sortBy(this.multiDates)
                for (let key in sortedMultiDates) {
                  const sortedMultiDate = sortedMultiDates[key]
                  const dateTime = moment(sortedMultiDate).format('YYYY-MM-DD') + ' ' + this.selectedTime
                  multiDatesTimes.push(dateTime)
                  // Date-times for displaying in message
                  const displayDateTime = this.$options.filters.shortDate(sortedMultiDate) + ' • ' + this.selectedTime
                  displayDatesTimes += displayDateTime + '<br>'
                }
              } else { // Has professional selection
                for (let key in this.mapDatesTimes) {
                  const selectedTime = this.mapDatesTimes[key]
                  const dateTime = key + ' ' + selectedTime
                  multiDatesTimes.push(dateTime)
                  // Date-times for displaying in message
                  const displayDateTime = this.$options.filters.shortDate(key) + ' • ' + selectedTime
                  displayDatesTimes += displayDateTime + '<br>'
                }
              }
              action = {
                label: 'selected-date-time',
                text: displayDatesTimes.toString(),
                data: `selected_dates_times=${multiDatesTimes.toString()}&package_type_id=${this.packageType.id}`
              }
              if (this.packageTypeDiscount) {
                action.data += `&package_type_discount_id=${this.packageTypeDiscount.id}`
              }
            } else { // Check one-time booking
              let selectedDateTime = this.selectedDate + ' ' + this.selectedTime
              selectedDateTime = moment(selectedDateTime, 'YYYY-MM-DD HH:mm').format('DD MMM YYYY (HH:mm)')
              action = {
                label: 'selected-date-time',
                text: selectedDateTime,
                data: `cleaning_date=${this.selectedDate}&cleaning_time=${this.selectedTime}`
              }
            }
          } else { // Flexible booking
            if (this.bookingType === 'flexible' && !this.packageType) {
              action = {
                label: 'selected-date-time',
                text: this.$i18n.t('message.booking.new_booking.booking_type_flexible'),
                data: null
              }
            } else if (this.packageType && this.packageType.category === 'flexible') {
              let text = this.$i18n.t('message.booking.new_booking.package_type_flexible')
              text += '<br>'
              text += this.packageTypeDiscount.name_th
              action = {
                label: 'selected-date-time',
                text: text,
                data: `package_type_id=${this.packageType.id}`
              }
              if (this.packageTypeDiscount) {
                action.data += `&package_type_discount_id=${this.packageTypeDiscount.id}`
              }
            }
          }
          this.sendMessage(action)
        }
      }
    },
    async mounted () {
      // Load global data to config disable same day booking
      const payload = {unlimited_package: 1, user_id: this.user.id}
      await this.$store.dispatch('globalVal/LOAD_GLOBAL_DATA', payload)
      if (this.bookingData) {
        this.parentService = serviceHelpers.getParentService(this.globalState.services, Number(this.bookingData.service_id))
      }
      this.sameDayBookingConfig = serviceHelpers.formatSameDayData(this.globalState.configData.same_day_booking)
      if (this.parentService && this.sameDayBookingConfig.services) {
        this.sameDayBookingConfig = serviceHelpers.getSameDayService(this.sameDayBookingConfig, this.parentService.id, Number(this.bookingData.province_id))
      }
      if (this.bookingData) {
        this.workHours = Number(this.bookingData.work_hour)
        this.professionalId = (this.bookingData.professional_id) ? Number(this.bookingData.professional_id) : 0
        if (this.professionalId > 0) {
          await this.fetchProfessionalCalendar()
        } else {
          this.initDatePicker()
        }
      }
      // Set route from query params
      const queryParams = this.$route.query
      if (queryParams.route) {
        this.route = queryParams.route
      }
      this.language = this.$i18n.locale
    }
  }
</script>

<style scoped>
  @import '~air-datepicker/dist/css/datepicker.min.css';
</style>



// WEBPACK FOOTER //
// src/components/chatBooking/components/DateTimeMessage.vue